/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DataOptions } from "vuetify";
import {
  AttributeTemplate,
  AttributeTemplateResponse,
} from "@/models/api/administration/servicesAndAttributes/AttributeTemplate";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import {
  addAttributeTemplate,
  editAttributeTemplate,
  getAttributeTemplates,
  removeAttributeTemplate,
} from "@/requests/administration/servicesAndAttributes/administrationAttributeTemplates";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { AttributeTemplateAddEdit } from "@/models/api/administration/servicesAndAttributes/AttributeTemplateAddEdit";
import { ServiceTypeListItem } from "@/models/api/serviceTypes/ServiceTypeListItem";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import {
  addServiceTypeAttributeTemplate,
  removeServiceTypeAttributeTemplate,
} from "@/requests/administration/servicesAndAttributes/administrationServiceTypes";

export interface AdministrationAttributeTemplatesStateInterface {
  filters: null | Array<CriterionBase>;
  dataTableOptions: DataOptions;
  attributes: Array<AttributeTemplate>;
  selectedAttributeId: null | number;
  countItems: number | null;
  isAddEditDialog: boolean;
  attributeTemplateAddEdit: AttributeTemplateAddEdit;
  attributeTemplateAddEditId: null | number;
  isAssignDialog: boolean;
  selectedServiceTypesToAssignAttribute: Array<ServiceTypeListItem>;
  isUnassignDialog: boolean;
  selectedServiceTypesToUnassignAttribute: Array<ServiceTypeListItem>;
}

export const AdministrationAttributeTemplatesState: AdministrationAttributeTemplatesStateInterface = {
  filters: [],
  dataTableOptions: new DefaultDataOptions(),
  attributes: [],
  selectedAttributeId: null,
  countItems: null,
  isAddEditDialog: false,
  attributeTemplateAddEdit: new AttributeTemplateAddEdit(),
  attributeTemplateAddEditId: null,
  isAssignDialog: false,
  selectedServiceTypesToAssignAttribute: [],
  isUnassignDialog: false,
  selectedServiceTypesToUnassignAttribute: [],
};

const AdministrationAttributeTemplatesModule = defineModule({
  state: AdministrationAttributeTemplatesState as AdministrationAttributeTemplatesStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setFilters(state, param: null | Array<CriterionBase>) {
      state.filters = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setAttributes(state, param: AttributeTemplateResponse) {
      state.attributes = param.items;
      state.countItems = param.totalCount;
    },
    setSelectedAttributeId(state, param: number | null) {
      state.selectedAttributeId = param;
    },
    setIsAddEditDialog(state, param: boolean) {
      state.isAddEditDialog = param;
    },
    setAttributeTemplateAddEdit(state, param: AttributeTemplateAddEdit) {
      state.attributeTemplateAddEdit = param;
    },
    setAttributeTemplateAddEditId(state, param: number | null) {
      state.attributeTemplateAddEditId = param;
    },
    setIsAssignDialog(state, param: boolean) {
      state.isAssignDialog = param;
    },
    setSelectedServiceTypesToAssignAttribute(state, param: Array<ServiceTypeListItem>) {
      state.selectedServiceTypesToAssignAttribute = param;
    },
    setIsUnassignDialog(state, param: boolean) {
      state.isUnassignDialog = param;
    },
    setSelectedServiceTypesToUnassignAttribute(state, param: Array<ServiceTypeListItem>) {
      state.selectedServiceTypesToUnassignAttribute = param;
    },
  },
  actions: {
    async downloadAttributes(context) {
      const { commit, state } = _moduleActionContext(context);
      const attributes = await getAttributeTemplates({
        count: state.dataTableOptions.itemsPerPage,
        page: state.dataTableOptions.page - 1,
        criterions: _.cloneDeep(state.filters),
        filterName: FILTER_NAMES.AttributeTemplateListFilter,
      });
      commit.setAttributes(attributes);
    },
    async removeAttributeTemplate(context, attributeTemplateId: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      if (await rootDispatch.popup.addConfirmRemoveElement()) {
        await removeAttributeTemplate(attributeTemplateId);
        await dispatch.downloadAttributes();
      }
    },
    async openNewAttributeTemplateDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setAttributeTemplateAddEdit(new AttributeTemplateAddEdit());
      commit.setAttributeTemplateAddEditId(null);
      commit.setIsAddEditDialog(true);
    },
    async openEditAttributeTemplateDialog(context, attributeTemplate: AttributeTemplate) {
      const { commit } = _moduleActionContext(context);
      commit.setAttributeTemplateAddEdit(AttributeTemplateAddEdit.fromAttributeTemplate(attributeTemplate));
      commit.setAttributeTemplateAddEditId(attributeTemplate.id);
      commit.setIsAddEditDialog(true);
    },
    async saveAttributeTemplate(context) {
      const { state, dispatch, commit, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.attributeTemplateAddEditId !== null) {
        await editAttributeTemplate(state.attributeTemplateAddEdit, state.attributeTemplateAddEditId);
      } else {
        await addAttributeTemplate(state.attributeTemplateAddEdit);
      }
      commit.setIsAddEditDialog(false);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
      await dispatch.downloadAttributes();
    },
    async openAssignDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setSelectedServiceTypesToAssignAttribute([]);
      commit.setIsAssignDialog(true);
    },
    async assignTemplatesToServices(context) {
      const { state, commit, rootDispatch, rootCommit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.selectedServiceTypesToAssignAttribute.length === 0) {
        rootDispatch.popup.addSnackbar(
          new SnackbarModel(filterCapitalize(i18n.t("selectAnServiceTypesFirst")), "error")
        );
      } else {
        await addServiceTypeAttributeTemplate(
          state.selectedServiceTypesToAssignAttribute.map((v) => v.id as number),
          [state.selectedAttributeId as number]
        );
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        commit.setIsAssignDialog(false);
        rootCommit.administration.serviceTypes.riseChangesCountServiceTypeAttributeTemplate();
      }
    },
    async openUnassignDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setSelectedServiceTypesToUnassignAttribute([]);
      commit.setIsUnassignDialog(true);
    },
    async unassignTemplatesFromServices(context) {
      const { state, commit, rootDispatch, rootCommit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.selectedServiceTypesToUnassignAttribute.length === 0) {
        rootDispatch.popup.addSnackbar(
          new SnackbarModel(filterCapitalize(i18n.t("selectAnServiceTypesFirst")), "error")
        );
      } else {
        await removeServiceTypeAttributeTemplate(
          state.selectedServiceTypesToUnassignAttribute.map((v) => v.id as number),
          [state.selectedAttributeId as number]
        );
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        commit.setIsUnassignDialog(false);
        rootCommit.administration.serviceTypes.riseChangesCountServiceTypeAttributeTemplate();
      }
    },
  },
});

export default AdministrationAttributeTemplatesModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, AdministrationAttributeTemplatesModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, AdministrationAttributeTemplatesModule);
